import { useMutation, useQueryClient } from '@tanstack/react-query';
import { claimTask, releaseTask, type Task } from '../api';
import { useAssignees } from '../useAssignees';
import { useProjectRole } from '../roles';

/**
 * Who has this task, and — on a row or a card — the one press that claims it.
 *
 * Once work can belong to someone other than its author, "who has this" is the first question
 * asked of any shared row — and the app had nowhere to say it. **Unclaimed** is a state with its
 * own treatment rather than an empty space: it is the thing a project view is scanned for, and
 * without it a project backlog is indistinguishable from a list of work already in hand.
 *
 * **Nothing at all outside a project.** Personal work gains no new furniture: there is only ever
 * one person it could belong to, so a chip saying so is a row's worth of width spent on a fact
 * nobody was in doubt about.
 *
 * The assignee is a name, not a hue — the pill reads the same to someone who cannot see its tone
 * (SC 1.4.1) — and it uses the existing pill vocabulary rather than a new chip, so it obeys the
 * row's slot and drop order and adds no truncation rule of its own.
 */
export function AssigneeChip({
  task,
  myUserId,
  name,
  /** False where the viewer's role could not act on it, so no control is offered that would 403. */
  actionable = true,
  /**
   * Whether to offer "Hand back" on a task you hold. Only the panel and the full view pass it.
   *
   * Off by default, so a row or a card has to opt *in* to putting a control that releases your
   * claim into a run of facts — and none does. See the comment where the button renders.
   */
  offerHandBack = false,
}: {
  task: Pick<Task, 'id' | 'projectId' | 'assigneeUserId'>;
  myUserId: string | null;
  name: string | null;
  actionable?: boolean;
  offerHandBack?: boolean;
}) {
  const queryClient = useQueryClient();
  const claim = useMutation({
    mutationFn: () => claimTask(task.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const release = useMutation({
    mutationFn: () => releaseTask(task.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (task.projectId === null) return null;

  const mine = task.assigneeUserId !== null && task.assigneeUserId === myUserId;

  if (task.assigneeUserId === null) {
    return (
      <span className="assignee" data-testid="assignee">
        <span className="pill warn" data-testid="unclaimed">
          unclaimed
        </span>
        {actionable && (
          <button
            className="take-on"
            onClick={(e) => {
              // The row underneath opens the task; this claims it. Only one may win.
              e.stopPropagation();
              claim.mutate();
            }}
            disabled={claim.isPending}
          >
            Take it on
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="assignee" data-testid="assignee">
      {/*
        "you" rather than your own name.

        On a list of your own work, your name repeated down every row is the least informative
        column on the screen. The name matters when it is somebody else's.
      */}
      <span className="pill" title={name ?? undefined}>
        {mine ? 'you' : (name ?? 'someone else')}
      </span>
      {/*
        Only where the line is labelled — the panel and the full view, never a row or a card.

        On a row this was first the loudest repeating text on the Tasks screen (15px, the title's
        near-black, on eight of thirteen rows); then `.link.subtle`, indistinguishable from the inert
        chips, so a misclick un-assigned silently (`01a08097-107d`); then bordered, which made it the
        one outlined word in the run, and the next round photographed exactly that (`01a15a31-591f`).
        Each fix answered the last round and fed the next, because the defect was the placement: a
        row's job is to say *who has this*, and changing it is the panel's. Below 60rem the row had
        already been kept free of it on those grounds; this holds the rule at every width.

        `Take it on` above is not gated the same way. On an unclaimed project row it is the primary
        act — the thing a project view is scanned for — where this is a rare reversal of your own
        claim. Bulk edit's Assignee menu offers "Nobody (unclaim)" for many rows at once.
      */}
      {actionable && offerHandBack && mine && (
        <button
          className="hand-back"
          onClick={(e) => {
            e.stopPropagation();
            release.mutate();
          }}
          disabled={release.isPending}
          title="Put it back in the project’s unclaimed pile"
        >
          Hand back
        </button>
      )}
    </span>
  );
}

/**
 * The same chip, on a surface with room to label it.
 *
 * A row has to be read at a glance and a panel is read deliberately, so the panel says which
 * question the pill is answering. Same component underneath, so the four surfaces cannot drift —
 * which is the failure the parity spec exists to catch.
 */
export function AssigneeLine({
  task,
}: {
  task: Pick<Task, 'id' | 'projectId' | 'assigneeUserId'>;
}) {
  const { myUserId, nameFor } = useAssignees([task]);
  /*
    The role gate the list has had since it was written, and the panel never did.

    `actionable` defaults to `true` and both detail surfaces took the default, so the two rows
    that check it — `TaskList` and `TaskBoard` — hid "Take it on" from a reporter while the panel
    and the full view kept offering it one click deeper. That is the control the persona pressed.
  */
  const { can } = useProjectRole(task.projectId);
  if (task.projectId === null) return null;
  return (
    <p className="assignee-line">
      <span className="subtext">Held by</span>{' '}
      <AssigneeChip
        task={task}
        myUserId={myUserId}
        name={nameFor(task.assigneeUserId)}
        actionable={can('assign')}
        offerHandBack
      />
    </p>
  );
}
